// @ts-strict-ignore
import React, {ReactNode} from 'react';
import {AxisTypeValue} from 'highcharts';
import clamp from 'lodash/clamp';
import cloneDeep from 'lodash/cloneDeep';
import mergeWith from 'lodash/mergeWith';

import {InputReact} from 'common/components/input';
import {
  BehaviorMetric,
  BookmarkXAxisOptions,
  BookmarkYAxisOptions,
  ShowClause,
} from 'common/report/insights/util/types';
import {ToggleReact} from 'common/components/toggle';
import {AxisAssignment} from 'common/charts/types';
import {ToggleSize, ToggleTheme} from 'common/components/toggle/types';
import {BehaviorShowClause} from 'common/report/insights/models/show-clause';
import {displayOptionsMatchAxis, isBehaviorShowClause, isFormulaShowClause} from 'common/report/insights/models/utils';
import {InputSize} from 'common/components/input/types';
import {SvgIconReact} from 'common/components/svg-icon';
import {ICON_MAPPING} from 'common/query-builder/query-entry-section/util/entry-icon';
import {getAlphaLabelFromIndex} from 'common/util';
import {ButtonReact} from 'common/components/button';
import {ButtonLayout, ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {useTranslation} from 'common/i18n/hooks';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {getMetricEntryBehavior} from 'common/report/insights/util/events';
import {TooltipReact} from 'common/components/tooltip';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {MetricUnitsSelector} from 'iron/common/query-builder/chart-customization/metric-units';
import {axisSettingsHaveChanged, getDisplayOptionsFromLineChartYAxisOptions} from 'common/report/insights/charts/util';
import {EMPTY_BOOKMARK_Y_AXIS_OPTIONS} from 'common/report/insights/util/constants';
import {AnalysisType, ChartType, MetricType} from 'common/types/reports/bookmark';

import {getDefaultPrimaryAndSecondaryAxisOptions, getXAxisDefaultOptions} from '../utils';
import styles from '../index.module.styl';
import {CallingLocation, track, TrackingEvent} from '../tracking';
import {CLEAR_VALUE, UNSET_VALUE} from './constants';
import {ChartCustomizationAxisProps, ChartCustomizationUpdateType} from './types';

const NUMBER_BOUNDS = {
  MAX: Number.MAX_SAFE_INTEGER,
  MIN: Number.MIN_SAFE_INTEGER,
};

// This callback makes sure that if a value is UNSET_VALUE, it is set to
// UNSET_VALUE in the object. Lodash's default behavior ignores "undefined"
// values which will break the merge.
const mergeWithCallback = (_objValue, srcValue, key, object) => {
  if (srcValue === UNSET_VALUE) {
    object[key] = UNSET_VALUE;
  }
};

/**
 * Makes sure the value is a number within the bounds of the safe integer range
 * and also checks for unset values.
 * @param value
 * @returns null | NaN | number
 */
const getSafeNumber = (value: string | undefined) => {
  const numValue = Number(value);
  if (value === undefined || value === ``) {
    return CLEAR_VALUE;
  }
  return clamp(numValue, NUMBER_BOUNDS.MIN, NUMBER_BOUNDS.MAX);
};

type YAxisSettingProps = {
  axisSetting: BookmarkYAxisOptions;
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void;
  inheritedPrecision?: number;
};

enum AxisVisibility {
  Visible = `visible`,
  Hidden = `hidden`,
}

const getDisplayValue = (value: number | undefined) => (value === undefined || value === null ? `` : String(value));

const HeaderRow = ({children}: {children: ReactNode}) => {
  return <h1 className={styles.ccAxisLabel}>{children}</h1>;
};

const getEventName = (
  clauseType: MetricType,
  clauseName: string,
  clauseId: number,
  metricsContext: MetricsResource,
) => {
  if (clauseType === MetricType.Formula) {
    return clauseName || metricsContext.getOne(clauseId)?.value?.name;
  }
  const eventDefinition = eventDefinitionsStore.value?.[clauseName];
  return eventDefinition?.displayName || eventDefinition?.name || clauseName || clauseType;
};

const VisibilityRow = ({axisSetting, setAxisSetting}: YAxisSettingProps) => {
  const {t} = useTranslation();

  return (
    <div className={styles.row}>
      <div style={{gridColumn: 1}}>
        <label className={styles.ccSublabel}>{t(`chart-customization.axis`)}</label>
      </div>
      <div className={styles.singleInputColumn}>
        <ToggleReact
          className={styles.singleInput}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={[
            {label: t(`common.visible`), value: AxisVisibility.Visible},
            {label: t(`common.hidden`), value: AxisVisibility.Hidden},
          ]}
          selected={axisSetting.labels?.enabled === false ? AxisVisibility.Hidden : AxisVisibility.Visible}
          onSelect={(ev) => {
            setAxisSetting({labels: {enabled: ev.detail.selected === AxisVisibility.Visible}});
          }}
        />
      </div>
    </div>
  );
};

const LabelRow = ({
  axisSetting,
  setAxisSetting,
  placeholder,
}: {
  axisSetting: BookmarkYAxisOptions;
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void;
  placeholder?: string;
}) => {
  const {t} = useTranslation();

  return (
    <div className={styles.row}>
      <div style={{gridColumn: 1}}>
        <label className={styles.ccSublabel}>{t(`common.label`)}</label>
      </div>
      <div className={styles.singleInputColumn}>
        <InputReact
          ariaLabel={t(`common.label`)}
          className={styles.singleInput}
          size={InputSize.Medium}
          placeholder={placeholder}
          value={axisSetting.title?.text || ``}
          onChange={(ev) => setAxisSetting({title: {text: ev.detail.value || null}})}
        />
      </div>
    </div>
  );
};

const ToggleSoftMinMaxRow = ({axisSetting, setAxisSetting}: YAxisSettingProps) => {
  const {t} = useTranslation();

  return (
    <div className={styles.row}>
      <div style={{gridColumn: 1}}>
        <label className={styles.ccSublabel}>
          {t(`chart-customization.strict-threshold`)}
          <SvgIconReact icon='info' className={styles.infoIcon} />
          <TooltipReact>{t(`chart-customization.strict-threshold-tooltip`)}</TooltipReact>
        </label>
      </div>
      <div style={{gridColumn: `2 / span 2`}}>
        <ToggleReact
          className={styles.singleInput}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={[
            {label: t(`common.visible`), value: `true`},
            {label: t(`common.hidden`), value: `false`},
          ]}
          selected={(axisSetting.useSoftMinMax || false) + ``}
          onSelect={(ev) => {
            setAxisSetting({useSoftMinMax: ev.detail.selected === `true`});
          }}
        />
      </div>
    </div>
  );
};

const ScaleRow = ({axisSetting, setAxisSetting}: YAxisSettingProps) => {
  const {t} = useTranslation();

  return (
    <div className={styles.row}>
      <div style={{gridColumn: 1}}>
        <label className={styles.ccSublabel}>{t(`chart-customization.scale`)}</label>
      </div>
      <div style={{gridColumn: `2 / span 2`}}>
        <ToggleReact
          className={styles.singleInput}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={[
            {label: t(`reports.analysis-types.linear`), value: AnalysisType.Linear},
            {label: t(`reports.analysis-types.logarithmic`), value: AnalysisType.Logarithmic},
          ]}
          selected={axisSetting.type || AnalysisType.Linear}
          onSelect={(ev) => {
            setAxisSetting({type: ev.detail.selected as AxisTypeValue});
          }}
        />
      </div>
    </div>
  );
};

const ThresholdRow = ({
  axisSetting,
  setAxisSetting,
  defaultMinValue,
  defaultMaxValue,
}: YAxisSettingProps & {
  defaultMinValue?: number;
  defaultMaxValue?: number;
}) => {
  const {t} = useTranslation();

  const minPlaceholderValue = defaultMinValue !== undefined ? getDisplayValue(defaultMinValue) : undefined;
  const maxPlaceholderValue = defaultMaxValue !== undefined ? getDisplayValue(defaultMaxValue) : undefined;
  return (
    <div className={styles.row}>
      <div style={{gridColumn: 1}}>
        <label className={styles.ccSublabel}>{t(`chart-customization.threshold`)}</label>
      </div>
      <div style={{gridColumn: 2}}>
        <InputReact
          ariaLabel={t(`chart-customization.min`)}
          className={styles.twoInputColumn}
          size={InputSize.Medium}
          placeholder={minPlaceholderValue || t(`chart-customization.min`)}
          value={getDisplayValue(axisSetting.min)}
          onChange={(ev) => {
            const number = getSafeNumber(ev.detail.value);
            if (!isNaN(number)) {
              setAxisSetting({min: number});
            }
          }}
        />
      </div>
      <div style={{gridColumn: 3}}>
        <InputReact
          ariaLabel={t(`chart-customization.max`)}
          className={styles.twoInputColumn}
          size={InputSize.Medium}
          placeholder={maxPlaceholderValue || t(`chart-customization.max`)}
          value={getDisplayValue(axisSetting.max)}
          onChange={(ev) => {
            const number = getSafeNumber(ev.detail.value);
            if (!isNaN(number)) {
              setAxisSetting({max: number});
            }
          }}
        />
      </div>
    </div>
  );
};

export function ChartAxisCustomization(props: ChartCustomizationAxisProps) {
  const {t} = useTranslation();
  const metricsContext = useMetrics();
  const defaultXOptions = getXAxisDefaultOptions(props.bookmarkParams, dataGroupStore);
  const showClauses = props.bookmarkParams.sections.show || [];

  const {defaultPrimaryOptions, defaultSecondaryOptions} = getDefaultPrimaryAndSecondaryAxisOptions(
    props.bookmarkParams,
    metricsContext,
  );

  const primaryYSetting: BookmarkYAxisOptions = props.bookmarkParams?.displayOptions?.primaryYAxisOptions || {};

  const secondaryYSetting: BookmarkYAxisOptions = props.bookmarkParams?.displayOptions?.secondaryYAxisOptions || {};

  const xSetting: BookmarkXAxisOptions = props.bookmarkParams?.displayOptions?.xAxisOptions || {
    labels: {enabled: true},
  };

  const setPrimaryYSetting = (updated: Partial<BookmarkYAxisOptions>) => {
    props.onUpdate(
      ChartCustomizationUpdateType.PrimaryYAxis,
      {
        primaryYAxis: updated,
        secondaryYAxis: secondaryYSetting,
        xAxis: xSetting,
        newShowClauses: showClauses,
      },
      updated,
      primaryYSetting,
    );
  };

  const resetPrimaryYSetting = () => {
    setPrimaryYSetting(EMPTY_BOOKMARK_Y_AXIS_OPTIONS);
  };

  const updatePrimaryYSetting = (setting: Partial<BookmarkYAxisOptions>) => {
    const updated = mergeWith(cloneDeep(primaryYSetting), setting, mergeWithCallback);
    setPrimaryYSetting(updated);
  };

  const setSecondaryYSetting = (updated: Partial<BookmarkYAxisOptions>) => {
    props.onUpdate(
      ChartCustomizationUpdateType.SecondaryYAxis,
      {
        primaryYAxis: primaryYSetting,
        secondaryYAxis: updated,
        xAxis: xSetting,
        newShowClauses: showClauses,
      },
      updated,
      secondaryYSetting,
    );
  };

  const resetSecondaryYSetting = () => {
    setSecondaryYSetting(EMPTY_BOOKMARK_Y_AXIS_OPTIONS);
  };

  const updateSecondaryYSetting = (setting: Partial<BookmarkYAxisOptions>) => {
    const updated = mergeWith(cloneDeep(secondaryYSetting), setting, mergeWithCallback);
    setSecondaryYSetting(updated);
  };

  const setXSetting = (setting: Partial<BookmarkXAxisOptions>) => {
    const updated = mergeWith(cloneDeep(xSetting), setting);
    props.onUpdate(
      ChartCustomizationUpdateType.XAxis,
      {
        primaryYAxis: primaryYSetting,
        secondaryYAxis: secondaryYSetting,
        xAxis: updated,
      },
      setting,
      primaryYSetting,
    );
  };

  const getUpdatedClause = (oldClause: any, axisAssignment: AxisAssignment) => {
    const newClause = cloneDeep(oldClause);
    if (!newClause.display) {
      newClause.display = {axis: AxisAssignment.Primary};
    }
    newClause.display.axis = axisAssignment;
    return newClause;
  };

  const getUpdatedClauses = (
    hasNestedBehaviors: boolean,
    axisAssignment: AxisAssignment,
    idx: number,
    oldClauses: ShowClause[],
  ) => {
    const newClauses = cloneDeep(oldClauses);

    newClauses[idx] = getUpdatedClause(newClauses[idx], axisAssignment);

    if (hasNestedBehaviors) {
      const behaviors = (newClauses[idx] as BehaviorMetric)?.behavior?.behaviors || [];
      for (let i = 0; i < behaviors.length; i++) {
        behaviors[i] = getUpdatedClause(behaviors[i], axisAssignment);
      }
    }
    return newClauses;
  };

  const getClauseRow = (hasNestedBehaviors: boolean, idx: number, clause: ShowClause) => {
    const {clauseType, clauseName, clauseId} = isFormulaShowClause(clause)
      ? {clauseType: MetricType.Formula, clauseName: clause?.name, clauseId: clause?.id}
      : {
          clauseType: clause.behavior.type,
          clauseName: getMetricEntryBehavior(clause.behavior).name,
          clauseId: clause.behavior.id,
        };

    const display = clause.display;
    const svgIcon = ICON_MAPPING[clauseType];
    return (
      <div className={styles.axisAssignmentRow} key={idx}>
        <div style={{gridColumn: 1}}>
          <div style={{display: `flex`, alignItems: `center`}}>
            <ButtonReact className={styles.eventPrefix} theme={ButtonTheme.Tertiary} notFocusable>
              <span className={styles.prefixText}>{getAlphaLabelFromIndex(idx)}</span>
            </ButtonReact>
            {svgIcon && <SvgIconReact className={styles.metricIcon} icon={svgIcon} />}
            <label>{getEventName(clauseType, clauseName, clauseId, metricsContext)}</label>
          </div>
        </div>
        <ToggleReact
          style={{width: `60px`}}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={[
            {label: t(`chart-customization.left-short`), value: AxisAssignment.Primary},
            {label: t(`chart-customization.right-short`), value: AxisAssignment.Secondary},
          ]}
          selected={display?.axis || AxisAssignment.Primary}
          onSelect={(ev) => {
            const oldClauses = showClauses;
            const updatedClauses = getUpdatedClauses(
              hasNestedBehaviors,
              ev.detail.selected as AxisAssignment,
              idx,
              oldClauses,
            );

            // If assigning a metric to an axis that doesn't have any other metrics assigned to it, show the labels
            const allPrimary = !oldClauses.some((x) => displayOptionsMatchAxis(x.display, AxisAssignment.Secondary));
            const allSecondary = !oldClauses.some((x) => displayOptionsMatchAxis(x.display, AxisAssignment.Primary));
            const shouldShowPrimary = primaryYSetting.labels?.enabled === false && allSecondary;
            const shouldShowSecondary = secondaryYSetting.labels?.enabled === false && allPrimary;

            const updatedPrimary = shouldShowPrimary
              ? cloneDeep({...primaryYSetting, ...{labels: {enabled: true}}})
              : primaryYSetting;
            const updatedSecondary = shouldShowSecondary
              ? cloneDeep({...secondaryYSetting, ...{labels: {enabled: true}}})
              : secondaryYSetting;

            props.onUpdate(ChartCustomizationUpdateType.MetricAxisAssignment, {
              primaryYAxis: updatedPrimary,
              secondaryYAxis: updatedSecondary,
              xAxis: xSetting,
              newShowClauses: updatedClauses,
            });
          }}
        />
      </div>
    );
  };

  const xAxisDisabled = props.bookmarkParams.displayOptions.chartType === ChartType.FunnelSteps;

  const defaultAxisOptions = getDisplayOptionsFromLineChartYAxisOptions(defaultPrimaryOptions);

  const showPrimaryAxisReset = axisSettingsHaveChanged({current: primaryYSetting, defaults: defaultPrimaryOptions});
  const showSecondaryAxisReset = axisSettingsHaveChanged({
    current: secondaryYSetting,
    defaults: defaultSecondaryOptions,
  });

  return (
    <div elref='chartCustomizationAxis'>
      <div elref='leftAxis' className={styles.axisBlock} style={{marginTop: `16px`}}>
        <HeaderRow>
          {t(`chart-customization.left-y-axis`)}
          <ButtonReact
            class={showPrimaryAxisReset ? `` : styles.hideResetButton}
            disabled={!showPrimaryAxisReset}
            layout={ButtonLayout.Horizontal}
            onClick={() => {
              resetPrimaryYSetting();
              track(TrackingEvent.Reset, CallingLocation.Axis, {
                axis: primaryYSetting,
                axisAssignment: AxisAssignment.Primary,
              });
            }}
            size={ButtonSize.Small}
            theme={ButtonTheme.Tertiary}
          >
            {t(`chart-customization.reset`)}
          </ButtonReact>
        </HeaderRow>
        <div className={styles.axisSettingsBlock}>
          <VisibilityRow axisSetting={primaryYSetting} setAxisSetting={updatePrimaryYSetting} />
          <LabelRow axisSetting={primaryYSetting} setAxisSetting={updatePrimaryYSetting} />
          <MetricUnitsSelector
            axis={primaryYSetting}
            axisAssignment={AxisAssignment.Primary}
            callingLocation={CallingLocation.Axis}
            chartType={props.bookmarkParams.displayOptions?.chartType}
            defaultAxisOptions={defaultAxisOptions}
            elref={`leftMetricUnitsSelector`}
            setAxis={updatePrimaryYSetting}
          />
          <ThresholdRow
            axisSetting={primaryYSetting}
            setAxisSetting={updatePrimaryYSetting}
            defaultMinValue={defaultPrimaryOptions.softMin}
            defaultMaxValue={defaultPrimaryOptions.softMax}
          />
          <ToggleSoftMinMaxRow axisSetting={primaryYSetting} setAxisSetting={updatePrimaryYSetting} />
          <ScaleRow axisSetting={primaryYSetting} setAxisSetting={updatePrimaryYSetting} />
        </div>
      </div>

      <div elref='rightAxis' className={styles.axisBlock}>
        <HeaderRow>
          {t(`chart-customization.right-y-axis`)}
          <ButtonReact
            class={showSecondaryAxisReset ? `` : styles.hideResetButton}
            disabled={!showSecondaryAxisReset}
            layout={ButtonLayout.Horizontal}
            onClick={() => {
              resetSecondaryYSetting();
              track(TrackingEvent.Reset, CallingLocation.Axis, {
                axis: secondaryYSetting,
                axisAssignment: AxisAssignment.Secondary,
              });
            }}
            size={ButtonSize.Small}
            theme={ButtonTheme.Tertiary}
          >
            {t(`chart-customization.reset`)}
          </ButtonReact>
        </HeaderRow>
        <div className={styles.axisSettingsBlock}>
          <VisibilityRow axisSetting={secondaryYSetting} setAxisSetting={updateSecondaryYSetting} />
          <LabelRow axisSetting={secondaryYSetting} setAxisSetting={updateSecondaryYSetting} />
          <MetricUnitsSelector
            axis={secondaryYSetting}
            axisAssignment={AxisAssignment.Secondary}
            callingLocation={CallingLocation.Axis}
            defaultAxisOptions={getDisplayOptionsFromLineChartYAxisOptions(defaultSecondaryOptions)}
            elref={`rightMetricUnitsSelector`}
            setAxis={updateSecondaryYSetting}
          />
          <ThresholdRow
            axisSetting={secondaryYSetting}
            setAxisSetting={updateSecondaryYSetting}
            defaultMinValue={defaultSecondaryOptions.softMin}
            defaultMaxValue={defaultSecondaryOptions.softMax}
          />
          <ToggleSoftMinMaxRow axisSetting={secondaryYSetting} setAxisSetting={updateSecondaryYSetting} />
          <ScaleRow axisSetting={secondaryYSetting} setAxisSetting={updateSecondaryYSetting} />
        </div>
      </div>

      {!xAxisDisabled && (
        <div className={styles.axisBlock}>
          <HeaderRow>{t(`chart-customization.x-axis`)}</HeaderRow>
          <div className={styles.axisSettingsBlock}>
            <VisibilityRow axisSetting={xSetting} setAxisSetting={setXSetting} />
            <LabelRow
              axisSetting={xSetting}
              setAxisSetting={setXSetting}
              placeholder={defaultXOptions.title?.placeholder}
            />
          </div>
        </div>
      )}

      <div elref='axisAssignment'>
        <HeaderRow>{t(`chart-customization.axis-assignment`)}</HeaderRow>
        <div className={styles.axisSettingsBlock}>
          {showClauses.map((clause, clauseIdx) => {
            const behavior = (clause as BehaviorShowClause)?.behavior;
            const hasNestedBehaviors = isBehaviorShowClause(clause) && behavior?.behaviors?.length > 0;
            return getClauseRow(hasNestedBehaviors, clauseIdx, clause);
          })}
        </div>
      </div>
    </div>
  );
}
